import { graphqlClient } from '@/lib/graphql-client';
import { mergeWhere } from '@/lib/table-query';
import { runWorkflow } from '@/lib/workflow-webhook';
import { buildGetOvertimeLedgerQuery, buildGetOvertimeQuery } from '../graphql/queries';
import {
  GetOvertimeLedgerParams,
  GetOvertimeParams,
  OvertimeLedgerPagingResult,
  OvertimePagingResult,
  OvertimeRecord,
  PayoffOvertimeParams,
} from '../types/overtime.types';

/** Published webhook URL of the OVERTIME_PAYOFF workflow (blocks/workflows/overtime). */
const OVERTIME_PAYOFF_WEBHOOK_URL = import.meta.env.VITE_OVERTIME_PAYOFF_WEBHOOK_URL ?? '';

/**
 * Only balance rows (one per employee) carry `TotalRemainingOvertimeHourInSecond`; ledger rows leave it
 * unset. Same test as the attendance workflows' balance read.
 */
const BALANCE_ROWS = { TotalRemainingOvertimeHourInSecond: { $gt: -1e12 } };
/** Ledger rows always have a `Source`; balance rows never do. */
const LEDGER_ROWS = { Source: { $ne: null } };

/** Employees' balance rows of the active organization (the query builder adds the organization). */
export const getOvertimeList = async (
  params: GetOvertimeParams
): Promise<{ Overtimes: OvertimePagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  // Merged rather than spread, so a column filter on the remaining balance narrows the balance rows
  // instead of replacing their test.
  return graphqlClient.query({
    query: buildGetOvertimeQuery(mergeWhere(filter, BALANCE_ROWS), pageNo, pageSize, order),
  });
};

/** The balance row of one employee (by IAM user id), or null when they have no overtime yet. */
export const getOvertimeBalance = async (userId: string): Promise<OvertimeRecord | null> => {
  const result = await getOvertimeList({ pageNo: 1, pageSize: 1, filter: { UserId: userId } });
  return result.Overtimes.items[0] ?? null;
};

/** One employee's ledger rows, newest first unless `order` says otherwise. */
export const getOvertimeLedger = async ({
  userId,
  pageNo,
  pageSize,
  filter = {},
  order,
}: GetOvertimeLedgerParams): Promise<{ OvertimeLedger: OvertimeLedgerPagingResult }> =>
  graphqlClient.query({
    query: buildGetOvertimeLedgerQuery(
      mergeWhere(mergeWhere(filter, { UserId: userId }), LEDGER_ROWS),
      pageNo,
      pageSize,
      order
    ),
  });

/**
 * Pays off overtime through the OVERTIME_PAYOFF workflow (legacy UserOvertimePayoffCommand). The
 * workflow re-reads the employee's balance row, refuses more than the remaining balance, lowers it only
 * while it still holds the value it read, and records the PaidOff ledger entry (putting the balance back
 * if that fails). A refusal throws its translation key.
 */
export const payoffOvertime = ({ overtime, payoffSeconds, note }: PayoffOvertimeParams) =>
  runWorkflow(
    OVERTIME_PAYOFF_WEBHOOK_URL,
    'VITE_OVERTIME_PAYOFF_WEBHOOK_URL',
    { overtimeId: overtime.ItemId, payoffSeconds, note: note ?? '' },
    'OVERTIME_SAVE_FAILED'
  );
